import { PortableTextBlock } from "@sanity/types";
import { useQuery } from "@tanstack/react-query";
import SMaxWidthContainer from "components/framework/SMaxWidthContainer";

import CultureShowsDisplay from "components/frontpage/culture/CultureShowsDisplay";
import RichText from "components/richtext/RichText";
import { toRem } from "lib/client/fonts";
import { getCultureProgram } from "lib/client/movieinfo";
import { useBaseTitle } from "lib/client/useTitle";
import { setDefaultCacheHeaders } from "lib/server/cache";
import { fwpakkeQuery, getServerData } from "lib/server/sanity";
import { ICommonAppData, IProgramPage } from "lib/types/sanity";
import { GetServerSideProps, GetServerSidePropsContext } from "next";
import Head from "next/head";
import styled from "styled-components";

//#region [Other]
const PROGRAM_PAGE_QUERY = `*[_id == "kulturProgram"][0]`;
//#endregion

//#region [Other]getServerSideProps
export const getServerSideProps: GetServerSideProps = getServerData(async (context) => {
    setDefaultCacheHeaders(context as GetServerSidePropsContext);

    const frontpageData = await fwpakkeQuery<IProgramPage>(PROGRAM_PAGE_QUERY);

    return {
        props: {
            infoText: (frontpageData?.showInfoText && frontpageData?.topInfoText?.length > 0) ? frontpageData?.topInfoText : null,
            groupShows: frontpageData?.groupShows ?? null
        }, // will be passed to the page component as props

    }
});
//#endregion

//#region [Styles]
const SSectionHeader = styled.header`
	text-align: center;
	margin-top: 2rem;
	margin-bottom: 2rem;

	h1 {
		font-size: ${toRem(31)};
		text-transform: uppercase;
	}
`;

const SInfoText = styled(RichText)`
	text-align: left;
`;
//#endregion

//#region [Props]
type KulturProgramProps = {
    daysWithShows: string[] | undefined;
    appData: ICommonAppData;
    infoText: PortableTextBlock[] | null;
    groupShows?: boolean;
};
//#endregion

//#region [Component]
export default function KulturProgram({ appData, infoText, groupShows = false }: KulturProgramProps) {

    const title = useBaseTitle(appData.settings, appData.kulturProgram?.headingText ?? "Kulturprogram");

    const { isLoading, isError, data, error, isFetching } = useQuery({
        queryKey: ["culture-program"], // key
        queryFn: () => getCultureProgram(undefined, undefined, true), // loader function
        refetchOnWindowFocus: false,
        refetchInterval: 600000, // refretch automatically every 10 minutes
        staleTime: 599999,
    } // options
    );

    return <SMaxWidthContainer as="section">
        <Head>
            <title>{title}</title>
            <meta property="og:title" content={title} key="title" />
        </Head>
        <SSectionHeader>
            <h1>{appData.kulturProgram?.headingText ?? "Kulturprogrammet"}</h1>
            {infoText && <SInfoText value={infoText} disableCustomComponents />}

        </SSectionHeader>

        <div aria-busy={isFetching || isLoading}>
            <CultureShowsDisplay data={data} groupShows={groupShows} error={error} settings={appData.settings} isError={isError} isLoading={isLoading} hideMoreLink showDate />
        </div>




    </SMaxWidthContainer>
}
//#endregion